/**
 * Enhanced Offline Sync Service
 * Extends existing sync to handle authentication-based caching, setup menu state, and conflict resolution
 * Requirements: 9.1, 9.2, 9.6, 9.7, 9.8, 9.9
 */

import { useSyncStore } from '../store/syncStore';
import type { QueuedAction } from '../store/syncStore';
import { useOfflineAuthStore } from '../store/offlineAuthStore';
import { offlineReadingHistoryCache } from './offlineReadingHistoryCache';
import { apiClient } from '../api/client';
import { securityAuditLogger } from '../utils/securityAuditLogger';

interface SyncResult {
  success: boolean;
  syncedActions: number;
  failedActions: number;
  errors: Array<{ actionId: string; error: string }>;
  timestamp: number;
}

interface ConflictResolution {
  strategy: 'server-wins' | 'client-wins' | 'merge';
  resolvedData: any;
  conflictType: 'auth' | 'reading' | 'setup';
}

interface SetupMenuState {
  theme: string;
  nickname?: string;
  cardImageStyle?: string;
  lastUpdated: number;
}

export class EnhancedOfflineSyncService {
  private static instance: EnhancedOfflineSyncService;
  private syncInProgress = false;
  private readonly MAX_RETRY_COUNT = 3;
  private readonly RETRY_DELAY_BASE = 1000; // 1 second base delay
  private readonly SETUP_STATE_KEY = 'setup_menu_state';

  private constructor() {
    this.initializeEventListeners();
  }

  static getInstance(): EnhancedOfflineSyncService {
    if (!EnhancedOfflineSyncService.instance) {
      EnhancedOfflineSyncService.instance = new EnhancedOfflineSyncService();
    }
    return EnhancedOfflineSyncService.instance;
  }

  /**
   * Initialize event listeners for online/offline and sync triggers
   */
  private initializeEventListeners(): void {
    if (typeof window === 'undefined') return;

    // Listen for online event
    window.addEventListener('online', () => {
      this.handleOnlineEvent();
    });

    // Listen for offline event
    window.addEventListener('offline', () => {
      this.handleOfflineEvent();
    });

    // Listen for custom sync trigger
    window.addEventListener('online-sync-trigger', () => {
      this.performSync();
    });

    // Periodic sync check (every 5 minutes when online)
    setInterval(() => {
      if (navigator.onLine && !this.syncInProgress) {
        this.performSync();
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Handle online event - trigger sync and refresh auth
   */
  private async handleOnlineEvent(): Promise<void> {
    console.log('Connection restored - initiating sync');
    
    // Update stores
    useSyncStore.getState().setOnlineStatus(true);
    useOfflineAuthStore.getState().setOnlineStatus(true);

    // Refresh authentication status
    await this.refreshAuthenticationStatus();

    // Trigger sync
    await this.performSync();

    // Sync setup menu state
    await this.syncSetupMenuState();

    securityAuditLogger.logSecurityEvent('online_event_handled', {
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle offline event
   */
  private handleOfflineEvent(): void {
    console.log('Connection lost - entering offline mode');
    
    // Update stores
    useSyncStore.getState().setOnlineStatus(false);
    useOfflineAuthStore.getState().setOnlineStatus(false);

    // Check offline auth status
    useOfflineAuthStore.getState().checkOfflineAuth();

    securityAuditLogger.logSecurityEvent('offline_event_handled', {
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Perform full sync of queued actions
   */
  async performSync(): Promise<SyncResult> {
    // Check if user is authenticated before syncing
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('⏸️ Sync skipped: User not authenticated');
      return {
        success: false,
        syncedActions: 0,
        failedActions: 0,
        errors: [{ actionId: 'sync', error: 'User not authenticated' }],
        timestamp: Date.now()
      };
    }

    if (this.syncInProgress) {
      console.log('Sync already in progress, skipping');
      return {
        success: false,
        syncedActions: 0,
        failedActions: 0,
        errors: [{ actionId: 'sync', error: 'Sync already in progress' }],
        timestamp: Date.now()
      };
    }

    if (!navigator.onLine) {
      console.log('Cannot sync while offline');
      return {
        success: false,
        syncedActions: 0,
        failedActions: 0,
        errors: [{ actionId: 'sync', error: 'Device is offline' }],
        timestamp: Date.now()
      };
    }

    this.syncInProgress = true;
    useSyncStore.getState().startSync();

    const result: SyncResult = {
      success: true,
      syncedActions: 0,
      failedActions: 0,
      errors: [],
      timestamp: Date.now()
    };

    try {
      const queue = useSyncStore.getState().queue;
      const pendingActions = queue.filter(
        action => action.status === 'pending' || action.status === 'failed'
      );

      console.log(`Syncing ${pendingActions.length} pending actions`);

      for (const action of pendingActions) {
        try {
          await this.syncAction(action);
          result.syncedActions++;
        } catch (error) {
          result.failedActions++;
          result.errors.push({
            actionId: action.id,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Update last sync time
      useSyncStore.getState().setLastSyncTime(Date.now());

      // Sync reading history cache
      await this.syncReadingHistoryCache();

      // Sync setup menu state
      await this.syncSetupMenuState();

      securityAuditLogger.logSecurityEvent('sync_completed', {
        syncedActions: result.syncedActions,
        failedActions: result.failedActions,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Sync failed:', error);
      result.success = false;
      result.errors.push({
        actionId: 'sync',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      this.syncInProgress = false;
      useSyncStore.getState().endSync();
    }

    return result;
  }

  /**
   * Sync a single queued action
   */
  private async syncAction(action: QueuedAction): Promise<void> {
    const store = useSyncStore.getState();
    
    // Check retry count
    if (action.retryCount >= this.MAX_RETRY_COUNT) {
      console.warn(`Action ${action.id} exceeded max retries, marking as failed`);
      store.updateActionStatus(action.id, 'failed', 'Max retries exceeded');
      return;
    }

    // Update status to syncing
    store.updateActionStatus(action.id, 'syncing');

    try {
      // Execute the API call
      const response = await apiClient.request({
        method: action.method,
        url: action.endpoint,
        data: action.data
      });

      // Handle conflict resolution if needed
      if (response.status === 409) {
        const resolution = await this.resolveConflict(action, response.data);
        if (resolution) {
          // Retry with resolved data
          await apiClient.request({
            method: action.method,
            url: action.endpoint,
            data: resolution.resolvedData
          });
        }
      }

      // Mark as completed and remove from queue
      store.updateActionStatus(action.id, 'completed');
      store.removeFromQueue(action.id);

      securityAuditLogger.logSecurityEvent('action_synced', {
        actionId: action.id,
        actionType: action.type,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error(`Failed to sync action ${action.id}:`, error);
      
      // Increment retry count
      store.incrementRetryCount(action.id);
      
      // Calculate exponential backoff delay
      const delay = this.RETRY_DELAY_BASE * Math.pow(2, action.retryCount);
      
      // Update status to failed with error message
      store.updateActionStatus(
        action.id,
        'failed',
        error instanceof Error ? error.message : 'Unknown error'
      );

      // Schedule retry if under max retries
      if (action.retryCount < this.MAX_RETRY_COUNT) {
        setTimeout(() => {
          if (navigator.onLine) {
            this.syncAction(action);
          }
        }, delay);
      }

      throw error;
    }
  }

  /**
   * Resolve conflicts using server-wins strategy
   */
  private async resolveConflict(
    action: QueuedAction,
    serverData: any
  ): Promise<ConflictResolution | null> {
    console.log(`Resolving conflict for action ${action.id}`);

    // Default strategy: server wins
    const resolution: ConflictResolution = {
      strategy: 'server-wins',
      resolvedData: serverData,
      conflictType: this.getConflictType(action.type)
    };

    // For authentication conflicts, always use server data
    if (resolution.conflictType === 'auth') {
      await this.handleAuthConflict(serverData);
    }

    // For reading conflicts, merge if possible
    if (resolution.conflictType === 'reading') {
      resolution.resolvedData = this.mergeReadingData(action.data, serverData);
      resolution.strategy = 'merge';
    }

    // For setup conflicts, use server data but preserve local theme
    if (resolution.conflictType === 'setup') {
      resolution.resolvedData = this.mergeSetupData(action.data, serverData);
      resolution.strategy = 'merge';
    }

    securityAuditLogger.logSecurityEvent('conflict_resolved', {
      actionId: action.id,
      strategy: resolution.strategy,
      conflictType: resolution.conflictType,
      timestamp: new Date().toISOString()
    });

    return resolution;
  }

  /**
   * Get conflict type from action type
   */
  private getConflictType(actionType: QueuedAction['type']): ConflictResolution['conflictType'] {
    if (actionType === 'CREATE_READING' || actionType === 'UPDATE_READING' || 
        actionType === 'DELETE_READING' || actionType === 'FAVORITE_READING') {
      return 'reading';
    }
    return 'setup';
  }

  /**
   * Handle authentication conflicts
   */
  private async handleAuthConflict(serverData: any): Promise<void> {
    // Clear local auth state
    useOfflineAuthStore.getState().clearOfflineAuth();
    
    // If server has valid token, update local storage
    if (serverData.token) {
      localStorage.setItem('jwt_token', serverData.token);
      useOfflineAuthStore.getState().checkOfflineAuth();
    }

    securityAuditLogger.logSecurityEvent('auth_conflict_resolved', {
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Merge reading data (prefer server data for most fields)
   */
  private mergeReadingData(clientData: any, serverData: any): any {
    return {
      ...serverData,
      // Preserve client-side favorite status if more recent
      isFavorite: clientData.updatedAt > serverData.updatedAt 
        ? clientData.isFavorite 
        : serverData.isFavorite
    };
  }

  /**
   * Merge setup data (preserve local theme, use server for API-dependent settings)
   */
  private mergeSetupData(_clientData: any, serverData: any): any {
    const localSetupState = this.getLocalSetupState();
    
    return {
      ...serverData,
      // Always preserve local theme (non-API dependent)
      theme: localSetupState.theme,
      // Use server data for API-dependent settings
      nickname: serverData.nickname,
      cardImageStyle: serverData.cardImageStyle
    };
  }

  /**
   * Refresh authentication status from server
   */
  private async refreshAuthenticationStatus(): Promise<void> {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) return;

      // Verify token with server
      const response = await apiClient.get('/auth/verify');
      
      if (response.data.valid) {
        useOfflineAuthStore.getState().checkOfflineAuth();
      } else {
        // Token invalid on server, clear local auth
        useOfflineAuthStore.getState().clearOfflineAuth();
        localStorage.removeItem('jwt_token');
      }

      securityAuditLogger.logSecurityEvent('auth_status_refreshed', {
        valid: response.data.valid,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Failed to refresh auth status:', error);
    }
  }

  /**
   * Sync reading history cache with server
   */
  private async syncReadingHistoryCache(): Promise<void> {
    try {
      const authState = useOfflineAuthStore.getState();
      
      if (!authState.hasValidOfflineToken || !authState.offlineUserId) {
        console.log('No valid auth for reading history sync');
        return;
      }

      // Fetch latest readings from server
      const response = await apiClient.get('/readings', {
        params: { limit: 50, sort: 'createdAt:desc' }
      });

      if (response.data && response.data.readings) {
        await offlineReadingHistoryCache.cacheReadingHistory(
          authState.offlineUserId,
          response.data.readings,
          response.data.total || response.data.readings.length
        );

        console.log(`Synced ${response.data.readings.length} readings to cache`);
      }

    } catch (error) {
      console.error('Failed to sync reading history cache:', error);
    }
  }

  /**
   * Sync setup menu state with server
   */
  private async syncSetupMenuState(): Promise<void> {
    try {
      const localState = this.getLocalSetupState();
      
      // Fetch server state for API-dependent settings
      const response = await apiClient.get('/user/settings');
      
      if (response.data) {
        // Merge local and server state
        const mergedState: SetupMenuState = {
          theme: localState.theme, // Always use local theme
          nickname: response.data.nickname,
          cardImageStyle: response.data.cardImageStyle,
          lastUpdated: Date.now()
        };

        // Save merged state
        this.saveLocalSetupState(mergedState);

        console.log('Setup menu state synced');
      }

    } catch (error) {
      console.error('Failed to sync setup menu state:', error);
    }
  }

  /**
   * Get local setup menu state
   */
  private getLocalSetupState(): SetupMenuState {
    try {
      const stored = localStorage.getItem(this.SETUP_STATE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to get local setup state:', error);
    }

    // Return default state
    return {
      theme: 'system',
      lastUpdated: Date.now()
    };
  }

  /**
   * Save local setup menu state
   */
  private saveLocalSetupState(state: SetupMenuState): void {
    try {
      localStorage.setItem(this.SETUP_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save local setup state:', error);
    }
  }

  /**
   * Queue an action for sync
   */
  queueAction(action: Omit<QueuedAction, 'id' | 'timestamp' | 'retryCount' | 'status'>): void {
    useSyncStore.getState().addToQueue(action);
  }

  /**
   * Get sync status
   */
  getSyncStatus(): {
    isOnline: boolean;
    isSyncing: boolean;
    queueLength: number;
    lastSyncTime: number | null;
  } {
    const state = useSyncStore.getState();
    return {
      isOnline: state.isOnline,
      isSyncing: state.isSyncing,
      queueLength: state.queue.length,
      lastSyncTime: state.lastSyncTime
    };
  }

  /**
   * Clear sync queue (use with caution)
   */
  clearQueue(): void {
    useSyncStore.getState().clearQueue();
  }

  /**
   * Force sync now (if online)
   */
  async forceSyncNow(): Promise<SyncResult> {
    if (!navigator.onLine) {
      return {
        success: false,
        syncedActions: 0,
        failedActions: 0,
        errors: [{ actionId: 'sync', error: 'Device is offline' }],
        timestamp: Date.now()
      };
    }

    return await this.performSync();
  }
}

// Export singleton instance
export const enhancedOfflineSync = EnhancedOfflineSyncService.getInstance();
